/**
 * Global 3D barrel / wheel scroll engine.
 *
 * Two modes, opt-in via attribute:
 *
 *   data-barrel="spinner"   → Deep mechanical rotary-dial wheel.
 *                             Mobile + tablet only (< 1024px).
 *                             Children tilt up to ±45° on rotateX, edges
 *                             fade hard into shadow, soft-snap-to-center
 *                             after a flick.
 *
 *   data-barrel="tactile"   → Mechanical snapping product barrel.
 *                             All viewports including desktop.
 *                             CSS scroll-snap + amplified depth scale
 *                             (1.0 → 0.92) so the centered card "locks in".
 *
 *   data-barrel             → Legacy alias for "spinner" (kept for safety).
 *
 * Both modes use real document scroll. Hit-testing stays accurate because
 * the browser re-projects through the live 3D matrix every frame.
 *
 * Disabled when prefers-reduced-motion is set.
 */

const SPINNER_BREAKPOINT = 1024;

/* ── Spinner physics (manufacturers) ─────────────────────────────── */
const SP_PERSPECTIVE   = 900;
const SP_MAX_ANGLE     = 48;     // deg at the rim of the cylinder
const SP_MIN_SCALE     = 0.86;
const SP_MIN_OPACITY   = 0.18;   // edges sink deep into shadow
const SP_MIN_BRIGHT    = 0.55;
const SP_RANGE         = 1.05;   // half-viewports until peak distortion
const SP_SNAP_DELAY    = 150;    // ms after scroll stops

/* ── Tactile physics (products) ──────────────────────────────────── */
const TC_PERSPECTIVE   = 1400;
const TC_MAX_ANGLE     = 18;     // amplified tactile barrel, especially on touch screens
const TC_MIN_SCALE     = 0.88;   // stronger center pop
const TC_MIN_OPACITY   = 0.68;
const TC_RANGE         = 1.1;

const STYLE_TAG_ID = "fp-barrel-styles";
const CSS = `
/* ───────── Spinner mode — mobile + tablet only ───────── */
@media (max-width: ${SPINNER_BREAKPOINT - 1}px) {
  [data-barrel="spinner"], [data-barrel=""], [data-barrel]:not([data-barrel="tactile"]) {
    perspective: ${SP_PERSPECTIVE}px;
    perspective-origin: 50% 50%;
    transform-style: preserve-3d;
  }
  [data-barrel="spinner"] > *,
  [data-barrel=""] > *,
  [data-barrel]:not([data-barrel="tactile"]) > * {
    transform-origin: 50% 50%;
    will-change: transform, opacity, filter;
    backface-visibility: hidden;
    transition: none;
  }
}

/* ───────── Tactile mode — all viewports incl. desktop ───────── */
[data-barrel="tactile"] {
  perspective: ${TC_PERSPECTIVE}px;
  perspective-origin: 50% 50%;
  transform-style: preserve-3d;
  scroll-snap-type: y proximity;
}
[data-barrel="tactile"] > * {
  transform-origin: 50% 50%;
  will-change: transform, opacity;
  backface-visibility: hidden;
  transition: none;
  scroll-snap-align: center;
  scroll-snap-stop: always;
}
/* On mobile, harden the snap so cards "click" into place. */
@media (max-width: ${SPINNER_BREAKPOINT - 1}px) {
  [data-barrel="tactile"] { scroll-snap-type: y mandatory; }
}

@media (prefers-reduced-motion: reduce) {
  [data-barrel] > *,
  [data-barrel="tactile"] > * {
    transform: none !important;
    opacity: 1 !important;
    filter: none !important;
    scroll-snap-align: none !important;
  }
  [data-barrel="tactile"] { scroll-snap-type: none !important; }
}
`;

let installed = false;
let rafId: number | null = null;
let dirty = true;
let lastWidth = 0;
let snapTimer: number | null = null;

function injectStyles() {
  if (document.getElementById(STYLE_TAG_ID)) return;
  const s = document.createElement("style");
  s.id = STYLE_TAG_ID;
  s.textContent = CSS;
  document.head.appendChild(s);
}

function clearInline(el: HTMLElement) {
  el.style.transform = "";
  el.style.opacity = "";
  el.style.filter = "";
}

function applySpinner(container: HTMLElement, vh: number, vw: number) {
  if (vw >= SPINNER_BREAKPOINT) {
    // Clear leftover transforms if we crossed the breakpoint upward.
    for (const child of Array.from(container.children)) clearInline(child as HTMLElement);
    return;
  }
  const center = vh / 2;
  const halfH = vh / 2;
  const kids = container.children;
  for (let i = 0; i < kids.length; i++) {
    const el = kids[i] as HTMLElement;
    if (!el || el.nodeType !== 1) continue;
    const r = el.getBoundingClientRect();
    if (r.height === 0) continue;
    const childCenter = r.top + r.height / 2;
    let t = (childCenter - center) / halfH;
    if (t > SP_RANGE) t = SP_RANGE;
    if (t < -SP_RANGE) t = -SP_RANGE;
    const m = Math.min(1, Math.abs(t) / SP_RANGE);
    // Sine eases distortion near the center sweet spot.
    const eased = Math.sin((m * Math.PI) / 2);
    const angle = -t * SP_MAX_ANGLE * (0.6 + 0.4 * eased);
    const scale = 1 - eased * (1 - SP_MIN_SCALE);
    const opacity = 1 - eased * (1 - SP_MIN_OPACITY);
    const bright = 1 - eased * (1 - SP_MIN_BRIGHT);
    // translateZ pulls the centered item visually forward (rotary drum).
    const z = (1 - eased) * 24;
    el.style.transform =
      `translate3d(0, 0, ${z.toFixed(2)}px) rotateX(${angle.toFixed(2)}deg) scale(${scale.toFixed(3)})`;
    el.style.opacity = opacity.toFixed(3);
    el.style.filter = `brightness(${bright.toFixed(3)})`;
  }
}

function applyTactile(container: HTMLElement, vh: number) {
  const center = vh / 2;
  const halfH = vh / 2;
  const kids = container.children;
  for (let i = 0; i < kids.length; i++) {
    const el = kids[i] as HTMLElement;
    if (!el || el.nodeType !== 1) continue;
    const r = el.getBoundingClientRect();
    if (r.height === 0) continue;
    const childCenter = r.top + r.height / 2;
    let t = (childCenter - center) / halfH;
    if (t > TC_RANGE) t = TC_RANGE;
    if (t < -TC_RANGE) t = -TC_RANGE;
    const m = Math.min(1, Math.abs(t) / TC_RANGE);
    const eased = m * m;                                  // quadratic — center is a sweet spot
    const touchBoost = window.innerWidth < SPINNER_BREAKPOINT ? 2 : 1;
    const angle = -t * TC_MAX_ANGLE * touchBoost;
    const scale = 1 - eased * (1 - TC_MIN_SCALE) * touchBoost;
    const opacity = 1 - eased * (1 - TC_MIN_OPACITY);
    el.style.transform =
      `translateZ(${((1 - eased) * 18 * touchBoost).toFixed(2)}px) rotateX(${angle.toFixed(2)}deg) scale(${scale.toFixed(3)})`;
    el.style.opacity = opacity.toFixed(3);
  }
}

function tick() {
  rafId = null;
  if (!dirty) return;
  dirty = false;

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  lastWidth = vw;

  const containers = document.querySelectorAll<HTMLElement>("[data-barrel]");
  containers.forEach((container) => {
    const cr = container.getBoundingClientRect();
    if (cr.bottom < -300 || cr.top > vh + 300) return;
    const mode = container.getAttribute("data-barrel") || "spinner";
    if (mode === "tactile") {
      applyTactile(container, vh);
    } else {
      applySpinner(container, vh, vw);
    }
  });
}

function schedule() {
  dirty = true;
  if (rafId != null) return;
  rafId = requestAnimationFrame(tick);
}

/* Soft-snap the closest spinner child to viewport center after a flick. */
function snapSpinners() {
  const vw = window.innerWidth;
  if (vw >= SPINNER_BREAKPOINT) return;
  const vh = window.innerHeight;
  const center = vh / 2;
  // Find the spinner container whose midpoint is closest to viewport center.
  let bestContainer: HTMLElement | null = null;
  let bestContainerDist = Infinity;
  document.querySelectorAll<HTMLElement>('[data-barrel="spinner"], [data-barrel=""]').forEach((c) => {
    if (c.getAttribute("data-barrel") === "tactile") return;
    const r = c.getBoundingClientRect();
    if (r.bottom < 0 || r.top > vh) return;
    const d = Math.abs(r.top + r.height / 2 - center);
    if (d < bestContainerDist) { bestContainerDist = d; bestContainer = c; }
  });
  if (!bestContainer) return;
  const container: HTMLElement = bestContainer;
  let bestChild: HTMLElement | null = null;
  let bestDist = Infinity;
  for (const k of Array.from(container.children)) {
    const el = k as HTMLElement;
    const r = el.getBoundingClientRect();
    if (r.height === 0) continue;
    const d = Math.abs(r.top + r.height / 2 - center);
    if (d < bestDist) { bestDist = d; bestChild = el; }
  }
  if (!bestChild || bestDist < 6) return;
  const r = bestChild.getBoundingClientRect();
  const delta = r.top + r.height / 2 - center;
  // Only snap small residuals — never hijack a long scroll.
  if (Math.abs(delta) > vh * 0.42) return;
  window.scrollBy({ top: delta, behavior: "smooth" });
}

function onScroll() {
  schedule();
  if (snapTimer != null) window.clearTimeout(snapTimer);
  snapTimer = window.setTimeout(snapSpinners, SP_SNAP_DELAY);
}

export function installBarrelScroll() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  injectStyles();

  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", schedule, { passive: true });
  window.addEventListener("orientationchange", schedule, { passive: true });

  const mo = new MutationObserver(() => schedule());
  mo.observe(document.body, { childList: true, subtree: true });

  schedule();
  // Silence "unused" warnings for module-scope cache vars.
  void lastWidth;
}
